// src/components/QuestionnaireViewer.tsx
import { useEffect, useState } from 'react';
import { Button, Container, ProgressBar } from 'react-bootstrap';
import { supabase } from '../../lib/supabase';
import SectionViewer from './SectionViewer';
import type {
  AnswerValue,
  QuestionnaireTemplate,
  Section,
} from '../../types/types';
import { getLocalizedString } from '../../util/getLocalizedString';
import { captureSupabaseError } from '../../lib/sentry';
import { tracking } from '../../util/tracking';

interface QuestionnaireViewerProps {
  templateId?: string;
  assessmentId?: string;
}

export default function QuestionnaireViewer({
  templateId,
  assessmentId,
}: QuestionnaireViewerProps) {
  const [sections, setSections] = useState<Section[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [template, setTemplate] = useState<QuestionnaireTemplate>();
  const [resolvedTemplateId, setResolvedTemplateId] = useState<string | null>(
    templateId ?? null
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTemplateId = async () => {
      try {
        if (assessmentId) {
          // Step 1: get template_id from assessments
          const { data: assessmentData, error: assessmentError } =
            await supabase
              .from('assessments')
              .select('template_id')
              .eq('id', assessmentId)
              .eq('deleted', false)
              .maybeSingle();

          if (assessmentError) throw assessmentError;
          if (!assessmentData?.template_id)
            throw new Error('No template linked to this assessment.');

          setResolvedTemplateId(assessmentData.template_id);
        } else if (templateId) {
          setResolvedTemplateId(templateId);
        }
      } catch (err) {
        captureSupabaseError(err, { op: 'questionnaire.resolveTemplate' });
        console.error('Error resolving template ID:', err);
      }
    };

    fetchTemplateId();
  }, [assessmentId, templateId]);

  useEffect(() => {
    const fetchData = async () => {
      if (!resolvedTemplateId) return;
      setLoading(true);

      try {
        // Step 2: Fetch the questionnaire template
        const { data: templateData, error: templateError } = await supabase
          .from('questionnaire_templates')
          .select('*')
          .eq('id', resolvedTemplateId)
          .maybeSingle();

        if (templateError) throw templateError;
        if (templateData) setTemplate(templateData);

        // Step 3: Fetch sections and questions
        const { data: sectionsData, error: sectionsError } = await supabase
          .from('question_sections')
          .select('*')
          .eq('template_id', resolvedTemplateId)
          .eq('deleted', false)
          .order('ordinal', { ascending: true });

        if (sectionsError) throw sectionsError;

        const { data: questionsData, error: questionsError } = await supabase
          .from('questions')
          .select('*')
          .eq('template_id', resolvedTemplateId)
          .eq('deleted', false)
          .order('ordinal', { ascending: true });

        if (questionsError) throw questionsError;

        if (sectionsData && questionsData) {
          const sectionsWithQuestions = sectionsData.map((s) => ({
            ...s,
            questions: questionsData.filter((q) => q.section_id === s.id),
          }));
          setSections(sectionsWithQuestions);
        }
      } catch (err) {
        captureSupabaseError(err, { op: 'questionnaire.fetch' });
        console.error('Error loading questionnaire:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [resolvedTemplateId]);

  const handleAnswerChange = (questionId: string, value: AnswerValue) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  if (loading) return <p>Loading...</p>;
  if (!sections.length) return <p>No questions found for this template.</p>;

  const section = sections[currentIndex];
  const totalSections = sections.length;
  const progress = Math.round(((currentIndex + 1) / totalSections) * 100);

  return (
    <Container fluid style={{ maxWidth: '650px' }}>
      <h1>{template?.title}</h1>
      <p className="text-muted">{template?.description}</p>
      <div className="p-3">
        <div className="mb-3">
          <ProgressBar
            now={progress}
            label={`${currentIndex + 1} / ${totalSections}`}
          />
        </div>

        <h3>{getLocalizedString(section.title) ?? 'Section'}</h3>
        <p className="text-muted">{getLocalizedString(section.description)}</p>

        <SectionViewer
          section={section}
          answers={answers}
          onAnswerChange={handleAnswerChange}
        />

        <div className="d-flex justify-content-between mt-3">
          <Button
            variant="secondary"
            onClick={() => setCurrentIndex((i) => i - 1)}
            disabled={currentIndex === 0}
          >
            Previous
          </Button>

          {currentIndex < totalSections - 1 ? (
            <Button onClick={() => setCurrentIndex((i) => i + 1)}>Next</Button>
          ) : (
            <Button
              variant="success"
              onClick={() => {
                tracking.log('UI', 'questionnaire_submit', {
                  templateId: template?.id,
                  assessmentId,
                });
                console.log('Submit', answers);
              }}
            >
              Submit
            </Button>
          )}
        </div>
      </div>
    </Container>
  );
}
